import React, { useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { actualizarProducto } from '../services/productosService';
import { useCategorias } from '../context/CategoriasContext';
import { useToast } from '../context/ToastContext';
import { obtenerCategoria, obtenerFotoPrincipal, iconoCategoria } from '../utils/categorias';
import { optimizarFoto } from '../utils/imagen';
import { formatoCantidad, decimalesCantidad, redondearCantidad } from '../utils/unidades';
import { descargarCSV, imprimirPDF } from '../utils/exportar';
import { copiarTexto } from '../utils/compartir';
import { OPCIONES_UMBRAL, estaAgotado } from '../utils/stockBajo';
import CampoNumero from './CampoNumero';
import { usePlan } from './SoloPlan';
import { segunTienda } from '../utils/tienda';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const pill = (activo) => ({
  padding: '7px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
  border: `1px solid ${activo ? 'var(--color-primario,#DB2777)' : '#E5E7EB'}`,
  backgroundColor: activo ? 'var(--color-primario,#DB2777)' : 'white',
  color: activo ? 'white' : '#6B7280',
});

// Agrupa nombres sin importar mayúsculas/tildes/espacios ("Brasier" = "BRASIER").
const normNombre = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().replace(/\s+/g, ' ').toUpperCase();

const detalleProducto = (p) => [p.codigo, p.marca, p.talla && `Talla ${p.talla}`, p.color].filter(Boolean).join(' · ');

// Stock bajo: lista para GESTIONAR lo que se está acabando — ver agotados y
// por agotarse, reponer stock ahí mismo y sacar la lista para el proveedor.
export default function StockBajoView({ productos = [], umbral, onCambiarUmbral, onVolver, onVerProducto, onCambio }) {
  // Descargar en Excel/PDF es del plan Pro.
  const plan = usePlan();
  const puedeExportar = plan.tiene('exportar');
  const { categorias } = useCategorias();
  const { mostrarToast } = useToast();
  const avisoPlan = () => mostrarToast(segunTienda(`Descargar en Excel o PDF está disponible en el plan ${plan.planNecesario('exportar')}. Míralo en "Mi plan".`, 'Descargar en Excel o PDF no está incluido en tu plan actual.'), 'error', 6000);
  const [tipo, setTipo] = useState('todos'); // 'todos' | 'agotados' | 'por_agotarse'
  const [categoriaFiltro, setCategoriaFiltro] = useState(null);
  const [productoFiltro, setProductoFiltro] = useState(''); // nombre normalizado
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('stock');
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  const [reponiendo, setReponiendo] = useState(null); // id del producto
  const [cantidad, setCantidad] = useState('');
  const [guardando, setGuardando] = useState(false);

  const bajos = useMemo(() => productos.filter((p) => Number(p.stock || 0) <= umbral), [productos, umbral]);
  const agotados = bajos.filter(estaAgotado);
  const porAgotarse = bajos.filter((p) => !estaAgotado(p));

  // Resumen por categoría: cuántos sin stock y cuántos por agotarse.
  const resumenCategorias = useMemo(() => {
    const m = new Map();
    bajos.forEach((p) => {
      const k = p.categoria || 'Sin categoría';
      const r = m.get(k) || { nombre: k, agotados: 0, porAgotarse: 0, total: 0 };
      if (estaAgotado(p)) r.agotados += 1; else r.porAgotarse += 1;
      r.total += 1;
      m.set(k, r);
    });
    return [...m.values()].sort((a, b) => b.agotados - a.agotados || b.total - a.total);
  }, [bajos]);

  const enCategoria = (p) => !categoriaFiltro || (p.categoria || 'Sin categoría') === categoriaFiltro;

  // Productos (por nombre) dentro de la categoría elegida, con sus cuentas.
  const opcionesProducto = useMemo(() => {
    const m = new Map();
    bajos.filter(enCategoria).forEach((p) => {
      const k = normNombre(p.nombre);
      if (!k) return;
      const r = m.get(k) || { clave: k, nombre: (p.nombre || '').trim(), agotados: 0, total: 0 };
      if (estaAgotado(p)) r.agotados += 1;
      r.total += 1;
      m.set(k, r);
    });
    return [...m.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bajos, categoriaFiltro]);

  const elegirCategoria = (c) => { setCategoriaFiltro(c); setProductoFiltro(''); setSeleccionados(new Set()); };

  const texto = busqueda.trim().toLowerCase();
  const lista = useMemo(() => {
    const base = tipo === 'agotados' ? agotados : tipo === 'por_agotarse' ? porAgotarse : bajos;
    const r = base
      .filter(enCategoria)
      .filter((p) => !productoFiltro || normNombre(p.nombre) === productoFiltro)
      .filter((p) => !texto || [p.nombre, p.marca, p.codigo, p.talla, p.color].some((v) => (v || '').toLowerCase().includes(texto)));
    const porNombre = (a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es');
    if (orden === 'nombre') return [...r].sort(porNombre);
    if (orden === 'categoria') return [...r].sort((a, b) => (a.categoria || '').localeCompare(b.categoria || '', 'es') || porNombre(a, b));
    if (orden === 'recientes') return [...r].sort((a, b) => new Date(b.creado_en || 0) - new Date(a.creado_en || 0));
    return [...r].sort((a, b) => Number(a.stock || 0) - Number(b.stock || 0) || porNombre(a, b));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, agotados, porAgotarse, bajos, categoriaFiltro, productoFiltro, texto, orden]);

  // Lo que se exporta/comparte: los marcados, o toda la lista si no hay marcados.
  const marcados = lista.filter((p) => seleccionados.has(p.id));
  const aExportar = marcados.length > 0 ? marcados : lista;
  const todosMarcados = lista.length > 0 && marcados.length === lista.length;

  const alternar = (id) => setSeleccionados((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const alternarTodos = () => setSeleccionados(todosMarcados ? new Set() : new Set(lista.map((p) => p.id)));

  const abrirReponer = (p) => { setReponiendo(p.id); setCantidad(''); };

  const guardarReposicion = async (p) => {
    const cant = redondearCantidad(cantidad);
    if (!(cant > 0)) { mostrarToast('Escribe cuántas unidades llegaron', 'error'); return; }
    const nuevo = redondearCantidad(Number(p.stock || 0) + cant);
    setGuardando(true);
    try {
      await actualizarProducto(p.id, { stock: nuevo }, p, 'Reposición de stock');
      mostrarToast(`✅ ${p.nombre}: ahora hay ${formatoCantidad(nuevo, p)} en stock`, 'exito');
      setReponiendo(null);
      setCantidad('');
      onCambio && onCambio();
    } catch (e) {
      mostrarToast('No se pudo guardar la reposición. Intenta de nuevo.', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const nombreLista = tipo === 'agotados' ? 'Agotados' : tipo === 'por_agotarse' ? 'Por agotarse' : 'Stock bajo';
  const subtitulo = `${aExportar.length} producto(s) · ${categoriaFiltro || 'Todas las categorías'}${productoFiltro ? ` · ${productoFiltro}` : ''} · stock de ${umbral} o menos · ${new Date().toLocaleDateString('es-HN')}`;

  const filasExport = () => aExportar.map((p) => [
    p.codigo || '', p.nombre || '', p.marca || '', p.talla || '', p.color || '', p.categoria || '',
    formatoCantidad(p.stock || 0, p), Number(p.precio_costo || 0).toFixed(2), Number(p.precio_venta || 0).toFixed(2),
  ]);
  const ENCABEZADOS = ['Código', 'Producto', 'Marca', 'Talla', 'Color', 'Categoría', 'Stock', 'Costo', 'Precio venta'];

  const exportarExcel = () => {
    if (aExportar.length === 0) { mostrarToast('No hay productos en la lista', 'error'); return; }
    descargarCSV(`stock-bajo-${nombreLista.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}`, ENCABEZADOS, filasExport());
    mostrarToast(`Excel con ${aExportar.length} producto(s) descargado 📄`, 'exito');
  };

  const exportarPDF = () => {
    if (aExportar.length === 0) { mostrarToast('No hay productos en la lista', 'error'); return; }
    imprimirPDF({ titulo: `⚠️ ${nombreLista} — lista para reponer`, subtitulo, encabezados: ENCABEZADOS, filas: filasExport() });
  };

  // Texto corto para mandar al proveedor (o anotarse uno mismo).
  const textoProveedor = () => [
    `📋 Lista para reponer (${aExportar.length})`,
    '',
    ...aExportar.map((p) => `• ${p.nombre}${detalleProducto(p) ? ` (${detalleProducto(p)})` : ''} — quedan ${formatoCantidad(p.stock || 0, p)}`),
  ].join('\n');

  const copiarLista = async () => {
    if (aExportar.length === 0) { mostrarToast('No hay productos en la lista', 'error'); return; }
    const ok = await copiarTexto(textoProveedor());
    mostrarToast(ok ? 'Lista copiada 📋 Pégala donde quieras' : 'No se pudo copiar', ok ? 'exito' : 'error');
  };

  const enviarWhatsApp = () => {
    if (aExportar.length === 0) { mostrarToast('No hay productos en la lista', 'error'); return; }
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(textoProveedor())}`, '_blank', 'noopener,noreferrer');
  };

  const invertidoReponer = aExportar.reduce((s, p) => s + Number(p.precio_costo || 0), 0);

  const Tarjeta = ({ activo, onClick, titulo, valor, detalle, color }) => (
    <button onClick={onClick}
      style={{ textAlign: 'left', cursor: 'pointer', backgroundColor: 'white', borderRadius: '12px', padding: '14px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `2px solid ${activo ? color : '#F3F4F6'}` }}>
      <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>{titulo}</p>
      <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color }}>{valor}</p>
      <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF' }}>{detalle}</p>
    </button>
  );

  const btnAccion = { padding: '8px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: '1px solid #E5E7EB', backgroundColor: 'white', color: '#374151' };

  return (
    <div className="k-page">
      <div className="k-page-head">
        <h2 className="k-page-title">⚠️ Stock bajo — {bajos.length} productos</h2>
        <div className="k-actions">
          <button onClick={onVolver} className="k-btn-soft">← Volver al inventario</button>
        </div>
      </div>

      {/* Desde cuántas unidades se considera "bajo" */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '14px', fontSize: '13px', color: '#4B5563' }}>
        <span style={{ fontWeight: 600 }}>Avisarme cuando queden:</span>
        <select value={umbral} onChange={(e) => { onCambiarUmbral(Number(e.target.value)); setSeleccionados(new Set()); }}
          aria-label="Avisarme cuando queden"
          style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '13px', backgroundColor: 'white' }}>
          {OPCIONES_UMBRAL.map((n) => (
            <option key={n} value={n}>{n === 0 ? 'Solo agotados (0)' : `${n} o menos`}</option>
          ))}
        </select>
        <span style={{ color: '#9CA3AF', fontSize: '12px' }}>Se usa también en el aviso del menú y en Inicio.</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        <Tarjeta activo={tipo === 'todos'} onClick={() => setTipo('todos')} titulo="Todos con stock bajo" valor={bajos.length} detalle={`Stock de ${umbral} o menos`} color="#B45309" />
        <Tarjeta activo={tipo === 'agotados'} onClick={() => setTipo('agotados')} titulo="🔴 Agotados" valor={agotados.length} detalle="Ya no hay (stock 0)" color="#DC2626" />
        {umbral > 0 && (
          <Tarjeta activo={tipo === 'por_agotarse'} onClick={() => setTipo('por_agotarse')} titulo="🟡 Por agotarse" valor={porAgotarse.length} detalle={`Quedan entre 1 y ${umbral}`} color="#D97706" />
        )}
      </div>

      {/* Por categoría: sin stock / por agotarse. Tocar una filtra la lista. */}
      {resumenCategorias.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 700, color: '#374151' }}>Por categoría</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(165px, 1fr))', gap: '8px' }}>
            <button onClick={() => elegirCategoria(null)}
              style={{ ...pill(categoriaFiltro === null), borderRadius: '10px', textAlign: 'left', padding: '8px 12px' }}>
              Todas<br /><span style={{ fontSize: '12px', fontWeight: 500 }}>{agotados.length} sin stock · {porAgotarse.length} por agotarse</span>
            </button>
            {resumenCategorias.map((c) => {
              const cat = obtenerCategoria(categorias, c.nombre);
              const activa = categoriaFiltro === c.nombre;
              return (
                <button key={c.nombre} onClick={() => elegirCategoria(c.nombre)}
                  style={{ ...pill(activa), borderRadius: '10px', textAlign: 'left', padding: '8px 12px' }}>
                  {cat?.icono ? `${cat.icono} ` : ''}{c.nombre}<br />
                  <span style={{ fontSize: '12px', fontWeight: 500 }}>
                    <span style={{ color: activa ? 'white' : '#DC2626' }}>{c.agotados} sin stock</span>
                    {umbral > 0 && <> · <span style={{ color: activa ? 'white' : '#B45309' }}>{c.porAgotarse} por agotarse</span></>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <input type="search" placeholder="🔍 Buscar por nombre, marca, código, talla o color"
          value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="k-input"
          style={{ flex: '1 1 260px', marginBottom: 0 }} />
        <select value={productoFiltro} onChange={(e) => { setProductoFiltro(e.target.value); setSeleccionados(new Set()); }} aria-label="Filtrar por producto"
          style={{ padding: '10px', borderRadius: '10px', border: `1px solid ${productoFiltro ? 'var(--color-primario,#DB2777)' : '#D1D5DB'}`, fontSize: '13px', backgroundColor: 'white', maxWidth: '100%' }}>
          <option value="">Todos los productos</option>
          {opcionesProducto.map((o) => (
            <option key={o.clave} value={o.clave}>{o.nombre} — {o.agotados} sin stock de {o.total}</option>
          ))}
        </select>
        <select value={orden} onChange={(e) => setOrden(e.target.value)} aria-label="Ordenar"
          style={{ padding: '10px', borderRadius: '10px', border: '1px solid #D1D5DB', fontSize: '13px', backgroundColor: 'white' }}>
          <option value="stock">Menos stock primero</option>
          <option value="categoria">Por categoría</option>
          <option value="nombre">Por nombre (A-Z)</option>
          <option value="recientes">Más recientes</option>
        </select>
      </div>

      {/* Acciones sobre la lista (o sobre los marcados) */}
      <div style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '10px 12px', marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#92400E', cursor: 'pointer' }}>
          <input type="checkbox" checked={todosMarcados} onChange={alternarTodos} disabled={lista.length === 0} />
          {marcados.length > 0 ? `${marcados.length} marcado(s)` : `Marcar todos (${lista.length})`}
        </label>
        <span style={{ fontSize: '12px', color: '#B45309' }}>
          {marcados.length > 0 ? 'Las acciones usan solo los marcados' : 'Las acciones usan toda la lista'} · costo de 1 c/u: <strong>{fmt(invertidoReponer)}</strong>
        </span>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <button onClick={enviarWhatsApp} style={{ ...btnAccion, backgroundColor: '#25D366', borderColor: '#25D366', color: 'white' }}>💬 Enviar por WhatsApp</button>
          <button onClick={copiarLista} style={btnAccion}>📋 Copiar lista</button>
          <button onClick={puedeExportar ? exportarExcel : avisoPlan} style={btnAccion}>📄 Excel{puedeExportar ? '' : ' 🔒'}</button>
          <button onClick={puedeExportar ? exportarPDF : avisoPlan} style={btnAccion}>🖨️ PDF{puedeExportar ? '' : ' 🔒'}</button>
        </div>
      </div>

      {lista.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#6B7280' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎉</div>
          <p style={{ margin: 0 }}>{busqueda ? `Sin resultados para "${busqueda}".` : '¡Nada por reponer aquí!'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {lista.map((p) => {
            const agotado = estaAgotado(p);
            const foto = obtenerFotoPrincipal(p);
            const abierto = reponiendo === p.id;
            return (
              <div key={p.id} style={{ backgroundColor: 'white', borderRadius: '12px', padding: '10px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                border: `1px solid ${seleccionados.has(p.id) ? 'var(--color-primario,#DB2777)' : '#F3F4F6'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <input type="checkbox" checked={seleccionados.has(p.id)} onChange={() => alternar(p.id)} aria-label={`Marcar ${p.nombre}`} />
                  <button onClick={() => onVerProducto(p)} title="Ver producto"
                    style={{ width: '52px', height: '52px', borderRadius: '8px', overflow: 'hidden', border: 'none', padding: 0, cursor: 'pointer', backgroundColor: '#F9FAFB', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                    {foto
                      ? <img src={optimizarFoto(foto, 150)} alt={p.nombre} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : iconoCategoria(categorias, p.categoria)}
                  </button>
                  <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '14px', color: '#1F2937' }}>{p.nombre}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>
                      {detalleProducto(p) || '—'}{p.categoria ? ` · ${p.categoria}` : ''}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                      Costo {fmt(p.precio_costo)} · Venta {fmt(p.precio_venta)}
                    </p>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 700, padding: '4px 10px', borderRadius: '999px', whiteSpace: 'nowrap',
                    backgroundColor: agotado ? '#FEE2E2' : '#FEF3C7', color: agotado ? '#DC2626' : '#92400E' }}>
                    {agotado ? 'Agotado' : `Quedan ${formatoCantidad(p.stock, p)}`}
                  </span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => (abierto ? setReponiendo(null) : abrirReponer(p))}
                      style={{ ...btnAccion, borderColor: 'var(--k-verde,#059669)', color: 'var(--k-verde,#059669)' }}>
                      ➕ Reponer
                    </button>
                    <button onClick={() => onVerProducto(p)} style={btnAccion}>✏️ Ver / editar</button>
                  </div>
                </div>
                {abierto && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #E5E7EB' }}>
                    <span style={{ fontSize: '13px', color: '#374151' }}>¿Cuántas llegaron?</span>
                    <CampoNumero valor={cantidad} onChange={setCantidad} decimales={decimalesCantidad(p)} min={0}
                      deshabilitado={guardando} placeholder="Ej. 6"
                      estilo={{ width: '100px', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }} />
                    {Number(cantidad) > 0 && (
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>
                        Quedará en {formatoCantidad(redondearCantidad(Number(p.stock || 0) + Number(cantidad)), p)}
                      </span>
                    )}
                    <button onClick={() => guardarReposicion(p)} disabled={guardando}
                      style={{ ...btnAccion, backgroundColor: 'var(--k-verde,#059669)', borderColor: 'var(--k-verde,#059669)', color: 'white' }}>
                      {guardando ? 'Guardando…' : 'Guardar'}
                    </button>
                    <button onClick={() => setReponiendo(null)} disabled={guardando} style={btnAccion}>Cancelar</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
